
import React from "react";
import { Link } from "react-router-dom";

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <Link to="/" className="flex items-center gap-2 mb-6">
            <img src="/uploads/white-chair.png" alt="Figgy Logo" className="h-8 w-auto" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600">Effective Date: June 17, 2025</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <p>Aleph Studios LLC, doing business as Figgy ("we," "us," or "our") respects your privacy and is committed to protecting it through our compliance with this policy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services, including:</p>
          
          <ul>
            <li>Visiting our website at https://www.getfiggy.link</li>
            <li>Using our web or mobile application Figgy</li>
            <li>Engaging with us via support, marketing, or events</li>
          </ul>
          
          <p>If you do not agree with our policies and practices, your choice is not to use our Services. For any questions, contact us at hello@getfiggy.link.</p>

          <h2>1. Information We Collect</h2>
          
          <h3>Information You Provide</h3>
          <p>We collect personal information that you voluntarily provide to us when registering, expressing interest in our services, contacting support, or participating in activities. This includes:</p>
          <ul>
            <li>Contact info: name, phone, email, mailing address</li>
            <li>Authentication data: usernames, passwords</li>
            <li>Billing and payment data (via Stripe)</li>
            <li>Business info: job titles, booking preferences, business name</li>
            <li>Sensitive info: financial data (processed with consent)</li>
          </ul>
          
          <h3>Social Login Data</h3>
          <p>If you register using platforms like Google, we receive your profile info as permitted by that platform</p>
          
          <h3>Payment Data</h3>
          <p>Handled and stored by Stripe. See their privacy policy: https://stripe.com/privacy</p>
          
          <h3>Automatically Collected Information</h3>
          <p>We automatically collect:</p>
          <ul>
            <li>IP address</li>
            <li>Device info (type, OS, settings)</li>
            <li>Browser type</li>
            <li>Referring URLs</li>
            <li>Geolocation (with permission)</li>
            <li>Log/usage data (features used, crash reports, etc.)</li>
            <li>Cookies and tracking data</li>
          </ul>
          
          <h3>Application Data</h3>
          <p>With permission, we may access:</p>
          <ul>
            <li>Location services</li>
            <li>Calendar</li>
            <li>Contacts</li>
            <li>Notifications</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <p>We use the data collected to:</p>
          <ul>
            <li>Create/manage user accounts</li>
            <li>Process transactions, bookings, and subscriptions</li>
            <li>Respond to support requests</li>
            <li>Deliver notifications, updates, and communications</li>
            <li>Manage user-to-user messaging</li>
            <li>Conduct feedback surveys</li>
            <li>Send promotional emails (with opt-out)</li>
            <li>Prevent fraud and secure the platform</li>
            <li>Analyze usage and improve UX</li>
            <li>Protect user safety when necessary</li>
          </ul>

          <h2>3. Legal Bases for Processing (GDPR/UK GDPR/Canada)</h2>
          <p>Depending on jurisdiction, we may process your data based on:</p>
          <ul>
            <li>Your explicit consent</li>
            <li>Contractual necessity</li>
            <li>Legitimate business interest (e.g., fraud prevention, improvement)</li>
            <li>Legal compliance</li>
            <li>Vital interests (emergencies, harm prevention)</li>
          </ul>

          <h2>4. Sharing Your Information</h2>
          <p>We may share your information with:</p>
          <ul>
            <li>Cloud computing providers</li>
            <li>Hosting services</li>
            <li>Data analytics tools (e.g., Google Analytics)</li>
            <li>SMS/communication tools (e.g., Twilio)</li>
            <li>Payment processors (e.g., Stripe)</li>
            <li>Marketing platforms</li>
            <li>AI tools (limited to non-decision-making use)</li>
            <li>Customer support tools</li>
            <li>Government entities (as required)</li>
          </ul>
          
          <p>We do not sell personal data. All vendors operate under data processing agreements.</p>
          
          <p><strong>SMS Communications:</strong> Mobile information will not be shared with third parties/affiliates for marketing or promotional purposes. Information sharing with subcontractors for customer support is permitted. Opt-in consent and SMS data will never be sold or transferred.</p>

          <p>Text messaging originator opt-in data and consent will not be shared with any third parties, excluding aggregators and providers of the Text Message services.</p>
          
          <p><strong>Offer Wall:</strong> If you interact with third-party offer walls in our apps, unique IDs may be shared to credit rewards and prevent fraud.</p>
          
          <p><strong>Google API Use:</strong> Our use of Google API data complies with Google API Services User Data Policy.</p>

          <h2>5. Cookies & Tracking Technologies</h2>
          <p>We use cookies, web beacons, pixels, and similar tech to:</p>
          <ul>
            <li>Remember settings</li>
            <li>Authenticate sessions</li>
            <li>Track usage</li>
            <li>Run promotions and retargeting</li>
            <li>Improve site reliability</li>
          </ul>
          
          <p>You may reject or disable cookies via browser settings. See our Cookie Policy for details.</p>

          <h2>6. Use of AI Tools</h2>
          <p>We use AI tools for:</p>
          <ul>
            <li>Text analysis</li>
            <li>Internal feedback categorization</li>
          </ul>
          
          <p>We do not use AI for profiling or automated decisions. Your data is not used for model training.</p>

          <h2>7. Retention</h2>
          <p>We keep your data:</p>
          <ul>
            <li>As long as your account is active</li>
            <li>To comply with legal, audit, or regulatory obligations</li>
            <li>For up to 90 days post-account deletion (secure storage)</li>
          </ul>

          <h2>8. Security</h2>
          <p>We implement strong access controls, encryption, and monitoring. However, no online system is fully secure. Use our Services at your own risk.</p>

          <h2>9. Children's Privacy</h2>
          <p>We do not knowingly collect data from users under 18. If discovered, we delete it promptly. To report, email hello@getfiggy.link.</p>

          <h2>10. Your Rights</h2>
          <p>Depending on your jurisdiction, you may:</p>
          <ul>
            <li>Access, correct, delete, or export your data</li>
            <li>Withdraw consent</li>
            <li>Object or restrict processing</li>
            <li>Appeal decisions</li>
            <li>Opt out of marketing (unsubscribe or text STOP)</li>
          </ul>
          
          <p>Contact hello@getfiggy.link to exercise your rights.</p>

          <h2>11. U.S. State Privacy Rights</h2>
          <p>Applicable only to residents of Texas. You have:</p>
          <ul>
            <li>The right to access, correct, and delete personal info</li>
            <li>The right to limit or opt out of sensitive data use</li>
            <li>The right to not be discriminated against for exercising your rights</li>
          </ul>
          
          <p>We do not:</p>
          <ul>
            <li>Sell data</li>
            <li>Use biometric data</li>
            <li>Profile users for automated decisions</li>
          </ul>

          <h2>12. Do Not Track Signals</h2>
          <p>We do not currently respond to browser DNT signals due to lack of a standard. If this changes, we will update this policy.</p>

          <h2>13. Changes to This Policy</h2>
          <p>We may update this policy. The revised date will be shown at the top. Material changes may be communicated by email or in-app.</p>

          <h2>14. Additional Terms</h2>
          
          <h3>User Eligibility & Account Responsibility</h3>
          <p>You must be at least 18 years old to use the Services. By using Figgy, you represent and warrant that you meet this requirement. You are responsible for maintaining the confidentiality of your account login information and for all activities that occur under your account. We are not liable for any loss or damage arising from unauthorized use of your account.</p>
          
          <h3>License Grant</h3>
          <p>Subject to these Terms and our policies, we grant you a limited, non-transferable, non-exclusive, revocable license to access and use the Services solely for your personal or internal business use. You may not reproduce, distribute, modify, create derivative works of, publicly display, or reverse engineer any part of the Services unless expressly permitted.</p>
          
          <h3>Intellectual Property</h3>
          <p>All content, trademarks, logos, service marks, software code, and design elements of the Figgy web and mobile applications are the property of Aleph Studios LLC or its licensors, and are protected under applicable intellectual property laws. You may not copy, reproduce, modify, distribute, transmit, republish, display, or create derivative works from any part of the Services without express written permission.</p>
          
          <p>Your use of the Services does not grant you ownership of or license to any intellectual property rights except for the limited right to access and use the Services as provided under our Terms of Use.</p>
          
          <h3>Acceptable Use</h3>
          <p>You agree not to use the Services for any unlawful purpose or in a way that could harm, disable, or impair the Services. This includes:</p>
          <ul>
            <li>Uploading malicious code or content</li>
            <li>Attempting to gain unauthorized access to other accounts</li>
            <li>Scraping, crawling, or reverse engineering any part of the platform</li>
            <li>Using the Services to send spam or unsolicited messages</li>
          </ul>
          
          <p>We reserve the right to suspend or terminate accounts that violate this policy.</p>
          
          <h3>Termination</h3>
          <p>We reserve the right to suspend or terminate your access to the Services at any time, without notice, if you violate these terms or if your use poses a legal risk to us. Upon termination, all rights granted to you under these terms will cease.</p>
          
          <h3>Service Availability Disclaimer</h3>
          <p>The Services are provided on an "as-is" and "as-available" basis. We do not guarantee that the Services will be uninterrupted, timely, secure, or error-free. We may suspend or modify features without notice.</p>
          
          <h3>Dispute Resolution</h3>
          <p>By using the Services, you agree that any dispute or claim relating to this Privacy Policy or your use of the Services will be governed by the laws of the State of Texas, without regard to conflict of laws principles. Any legal action or proceeding will be brought exclusively in the courts located in Austin, Texas.</p>
          
          <h3>Limitation of Liability</h3>
          <p>To the maximum extent permitted by law, Aleph Studios LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data or other intangible losses, resulting from:</p>
          <ul>
            <li>Your use or inability to use the Services</li>
            <li>Unauthorized access to or use of your account or data</li>
            <li>Any conduct or content of any third party</li>
          </ul>
          
          <h3>Indemnification</h3>
          <p>You agree to indemnify, defend, and hold harmless Aleph Studios LLC and its affiliates, officers, agents, employees, and partners from any claim, liability, loss, damage, cost, or expense (including reasonable attorneys' fees) arising out of or related to your use of the Services or violation of this Privacy Policy or applicable law.</p>
          
          <h3>Force Majeure</h3>
          <p>We shall not be held liable for any failure or delay in performance caused by circumstances beyond our reasonable control, including but not limited to natural disasters, government actions, power failures, internet outages, or labor disputes.</p>

          <h2>15. Contact Us</h2>
          <p>
            Aleph Studios LLC<br />
            5900 Balcones Dr Ste 100<br />
            Austin, TX 78731<br />
            Email: hello@getfiggy.link
          </p>
        </div>
      </div>
    </div>
  );
}